import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Statistic, Button, Alert, Space, Select } from 'antd';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ProjectsApi, ContractorsApi, ContractorPaymentsApi } from 'api/api';
import {
  ContractorDetailsDto,
  AssignContractorToProjectCommand,
  ContractorProjectDetailsDto,
  CreateContractorPaymentCommand,
  ProjectDto,
} from 'api/models';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, TableWrapper } from 'container/styled';
import styled from 'styled-components';
import { AssignProjectModal } from './components/AssignProjectModal';
import { CreateContractorPaymentModal } from './components/CreateContractorPaymentModal';
import { ContractorPaymentsSummaryModal } from './components/ContractorPaymentsSummaryModal';
import { hasPermission } from 'utility/accessibility/hasPermission';
import { ContractorForm } from './ContractorForm';
import openNotificationWithIcon from 'utility/notification';
import { CustomTableStyleWrapper } from 'pages/style';
import { ContractorPaymentsTable } from './components/ContractorPaymentsTable';

const contractorsApi = new ContractorsApi();
const contractorPaymentsApi = new ContractorPaymentsApi();
const projectsApi = new ProjectsApi();

const Loader = styled.div`
  display: flex;
  height: 400px;
  width: 600px;
  justify-content: center;
  align-items: center;
`;

export const ContractorDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [contractor, setContractor] = useState<ContractorDetailsDto>();
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [assignProjectModalVisible, setAssignProjectModalVisible] = useState(false);
  const [projectsForAssignment, setProjectsForAssignment] = useState<ProjectDto[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [paymentsModalVisible, setPaymentsModalVisible] = useState(false);
  const [selectedSummaryProjectId, setSelectedSummaryProjectId] = useState<number | null>(null);
  const [selectedProjectFilter, setSelectedProjectFilter] = useState<number | undefined>(undefined);

  const fetchContractor = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await contractorsApi.apiContractorsIdGet({ id: parseInt(id) });
      setContractor(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContractor();
  }, [id]);

  useEffect(() => {
    if (assignProjectModalVisible) {
      fetchProjectsForAssignment();
    }
  }, [assignProjectModalVisible]);

  const fetchProjectsForAssignment = async () => {
    try {
      setProjectsLoading(true);
      const response = await projectsApi.apiProjectsGet({ pageSize: -1 });
      const activeProjectIds = contractor?.projects?.map(project => project.projectId) || [];
      const availableProjects = response.data.items!.filter(
        (project: ProjectDto) => !activeProjectIds.includes(project.id),
      );
      setProjectsForAssignment(availableProjects);
    } catch (error) {
      console.error(error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleUpdate = async (values: any) => {
    try {
      await contractorsApi.apiContractorsIdPut({
        id: parseInt(id!),
        updateContractorCommand: values,
      });
      openNotificationWithIcon('success', t('contractors:updated', 'Contractor updated successfully'));
      fetchContractor();
      setEditModalVisible(false);
    } catch (error) {
      openNotificationWithIcon('error', t('contractors:update-error', 'Error updating contractor'));
    }
  };

  const handleAssignProject = async (values: AssignContractorToProjectCommand) => {
    try {
      await contractorsApi.apiContractorsIdProjectsPost({
        id: parseInt(id!),
        assignContractorToProjectCommand: {
          ...values,
          contractorId: parseInt(id!),
        },
      });
      openNotificationWithIcon('success', t('contractors:project-assigned', 'Project assigned successfully'));
      fetchContractor();
      fetchProjectsForAssignment();
      setAssignProjectModalVisible(false);
    } catch (error) {
      openNotificationWithIcon('error', t('contractors:assign-error', 'Error assigning project'));
    }
  };

  const handleCreatePayment = async (values: any) => {
    try {
      const command: CreateContractorPaymentCommand = {
        contractorId: parseInt(id!),
        projectId: selectedProject!,
        amount: values.amount,
        dueDate: values.dueDate.toISOString(),
        paidDate: values.paidDate?.toISOString(),
        paymentMethod: values.paymentMethod,
        notes: values.notes,
      };

      await contractorPaymentsApi.apiContractorsContractorIdPaymentsPost({
        contractorId: parseInt(id!),
        createContractorPaymentCommand: command,
      });

      openNotificationWithIcon('success', t('payments:created', 'Payment created successfully'));
      setPaymentModalVisible(false);
      fetchContractor();
    } catch (error) {
      openNotificationWithIcon('error', t('payments:create-error', 'Error creating payment'));
    }
  };

  const handleViewPayments = (projectId: number) => {
    setSelectedSummaryProjectId(projectId);
    setPaymentsModalVisible(true);
  };

  const statistics = [
    {
      title: t('contractors:total-projects', 'Total Projects'),
      value: contractor?.statistics?.totalProjects || 0,
      icon: 'layers',
      color: '#1890ff',
    },
    {
      title: t('contractors:active-projects', 'Active Projects'),
      value: contractor?.statistics?.activeProjects || 0,
      icon: 'check-circle',
      color: '#52c41a',
    },
    {
      title: t('contractors:total-payments-received', 'Total Received'),
      value: `$${contractor?.statistics?.totalPaymentsReceived?.toFixed(2) || '0.00'}`,
      icon: 'dollar-sign',
      color: '#faad14',
    },
    {
      title: t('contractors:pending-payments', 'Pending Payments'),
      value: `$${contractor?.statistics?.pendingPayments?.toFixed(2) || '0.00'}`,
      icon: 'clock',
      color: '#f5222d',
    },
  ];

  const projectColumns = [
    {
      title: t('projects:name', 'Project Name'),
      dataIndex: 'projectName',
      render: (projectName: string, record: ContractorProjectDetailsDto) => (
        <Link to={`/projects/${record.projectId}`}>{projectName}</Link>
      ),
    },
    {
      title: t('contractors:investment-value', 'Investment Value'),
      dataIndex: 'investmentValue',
      render: (amount: number) =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'EUR',
        }).format(amount || 0),
    },
    {
      title: t('contractors:start-date', 'Start Date'),
      dataIndex: 'startDate',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: t('contractors:end-date', 'End Date'),
      dataIndex: 'endDate',
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : '-'),
    },
    {
      title: t('contractors:total-payments', 'Total Payments'),
      dataIndex: 'totalPayments',
      render: (amount: number) =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'EUR',
        }).format(amount || 0),
    },
    {
      title: t('contractors:pending-payments', 'Pending Payments'),
      dataIndex: 'pendingPayments',
      render: (amount: number) =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'EUR',
        }).format(amount || 0),
    },
    {
      title: t('actions', 'Actions'),
      key: 'actions',
      width: 80,
      align: 'center' as const,
      render: (record: ContractorProjectDetailsDto) => (
        <Button
          type="primary"
          shape="circle"
          size="small"
          title={t('payments:view-monthly-summary', 'View Monthly Payment Summary')}
          onClick={() => handleViewPayments(record.projectId as number)}
          icon={<FeatherIcon icon="calendar" size={14} />}
        />
      ),
    },
  ];

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={contractor?.name}
          subTitle={contractor?.phoneNumber}
          buttons={[
            hasPermission('contractors:edit') && (
              <>
                <Button
                  key="edit"
                  type="primary"
                  icon={<FeatherIcon icon="edit" size={16} />}
                  onClick={() => setEditModalVisible(true)}
                >
                  {t('global:edit', 'Edit')}
                </Button>

                <Button
                  key="stats"
                  type="ghost"
                  onClick={() => navigate(`/contractors/${id}/payments/summary`)}
                  icon={<FeatherIcon icon="bar-chart-2" size={16} />}
                >
                  {t('payments:stats', 'Payment Summary')}
                </Button>
              </>
            ),
          ]}
        />
      </CardToolbox>
      <Main>
        {contractor?.isInvestmentCovered && (
          <Alert
            className="mb-4"
            message={t(
              'contractors:investment-covered',
              'All project investments for this contractor have been fully covered through payments',
            )}
            type="success"
            showIcon
          />
        )}

        <Row gutter={[16, 16]} justify="center">
          {statistics.map((stat, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <Card loading={projectsLoading || loading} bordered={false} style={{ textAlign: 'center' }}>
                <FeatherIcon icon={stat.icon} size={24} style={{ marginBottom: 8, color: stat.color }} />
                <Statistic title={stat.title} value={stat.value} />
              </Card>
            </Col>
          ))}
        </Row>

        <Row gutter={[25, 25]} className="mt-4">
          <Col span={24}>
            <Card
              loading={projectsLoading || loading}
              title={t('contractors:projects', 'Active Projects')}
              extra={
                hasPermission('contractors:manage-payments') && (
                  <Button
                    type="primary"
                    icon={<FeatherIcon icon="plus" size={16} />}
                    onClick={() => setAssignProjectModalVisible(true)}
                  >
                    {t('contractors:assign-project', 'Assign Project')}
                  </Button>
                )
              }
            >
              <CustomTableStyleWrapper>
                <TableWrapper>
                  <Table
                    dataSource={contractor?.projects as ContractorProjectDetailsDto[]}
                    columns={projectColumns}
                    pagination={false}
                  />
                </TableWrapper>
              </CustomTableStyleWrapper>
            </Card>
          </Col>

          <Col span={24}>
            <Card
              loading={projectsLoading || loading}
              title={
                <Space size="large">
                  <div className="ant-card-head-title">{t('contractors:recent-payments', 'Recent Payments')}</div>
                  {(contractor?.projects?.length || 0) > 1 && (
                    <Select
                      style={{ width: 200 }}
                      placeholder={t('projects:select-filter', 'Filter by Project')}
                      onChange={value => setSelectedProjectFilter(value)}
                      value={selectedProjectFilter}
                      allowClear
                    >
                      <Select.Option value={-1}>{t('projects:all-projects', 'All Projects')}</Select.Option>
                      {contractor?.projects?.map(project => (
                        <Select.Option key={project.projectId} value={project.projectId}>
                          {project.projectName}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                </Space>
              }
              extra={
                hasPermission('contractors:manage-payments') && (
                  <Space>
                    <Button
                      type="primary"
                      icon={<FeatherIcon icon="plus" size={16} style={{ marginRight: 5 }} />}
                      onClick={() => setPaymentModalVisible(true)}
                    >
                      {t('payments:create', 'Create Payment')}
                    </Button>
                  </Space>
                )
              }
            >
              <ContractorPaymentsTable
                contractorId={parseInt(id!)}
                onPaymentChange={fetchContractor}
                projectId={selectedProjectFilter}
                projects={contractor?.projects || []}
              />
            </Card>
          </Col>
        </Row>

        {/* Edit Modal */}
        <ContractorForm
          visible={editModalVisible}
          editMode={true}
          contractor={contractor}
          onSuccess={handleUpdate}
          onCancel={() => setEditModalVisible(false)}
        />

        {/* Assign Project Modal */}
        <AssignProjectModal
          visible={assignProjectModalVisible}
          onCancel={() => setAssignProjectModalVisible(false)}
          onSubmit={handleAssignProject}
          projects={projectsForAssignment}
          loading={projectsLoading}
        />

        {/* Payment Creation Modal */}
        <CreateContractorPaymentModal
          visible={paymentModalVisible}
          onCancel={() => setPaymentModalVisible(false)}
          onSubmit={handleCreatePayment}
          projects={contractor?.projects || []}
          onProjectSelect={setSelectedProject}
        />

        {/* Project Monthly Payments Modal */}
        <ContractorPaymentsSummaryModal
          visible={paymentsModalVisible}
          onCancel={() => setPaymentsModalVisible(false)}
          contractorId={parseInt(id!)}
          projectId={selectedSummaryProjectId}
        />
      </Main>
    </>
  );
};
